import type { CommentaryTemplateDto } from "@eu.mdotm.api/sphere";
import { PageHeader } from "$root/components/PageHeader";
import { typedUrlForRoute, useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import ReactQueryWrapper from "$root/components/ReactQueryWrapper";
import { spawnDeleteDialog } from "$root/components/spawnable/entity-management/delete-dialog";
import { spawnDuplicateDialog } from "$root/components/spawnable/entity-management/duplicate-dialog";
import { spawnRenameDialog } from "$root/components/spawnable/entity-management/rename-dialog";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { storyfolioRepository } from "$root/functional-areas/storyfolio/repository";
import { platformToast } from "$root/notification-system/toast";
import { actionsColumn } from "$root/ui-lib/interactive-collections/common-table-actions";
import { builtInCaseInsensitiveSortFor, parallelize, qualifier } from "$root/utils";
import type { Abortable, MaybePromise } from "@mdotm/utils/promise";
import { Card } from "$root/widgets-architecture/layout/Card";
import {
	type OrderBy,
	type TableColumn,
	ActionText,
	Badge,
	BaseHScrollTable,
	Controller,
	DebouncedSearchInput,
	Icon,
	TableDataCell,
	TableHeadCell,
	sortRows,
	useSelectableTableColumn,
} from "@mdotm/shadcn/legacy/components";
import { useSearchable, type MultiSelectCtx } from "@mdotm/shadcn/legacy/headless";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { builtInSortFnFor, noop } from "@mdotm/shadcn/legacy/utils";
import { useMemo, useState } from "react";
import { BulkActions, Button, IconButton } from "@mdotm/shadcn/mdotm/components";

function StoryfolioStudio(): React.JSX.Element {
	const { push } = useTypedNavigation();

	return (
		<ReactQueryWrapper {...storyfolioRepository.templates.getQueryConfig()}>
			{(rows, query) => (
				<StoryfolioStudioInner
					rows={rows}
					checkIfNameIsAvailable={storyfolioRepository.isCommentaryNameIsAvailable}
					onRename={async (uuid, name, row) => {
						await storyfolioRepository.update(uuid, { ...row, name });
						await query.refetch();
					}}
					onBulkDelete={async (uuids, ctx) => {
						let successCounter = 0;
						await parallelize(
							uuids.map((uuid) => () => storyfolioRepository.delete(uuid).then(() => successCounter++)),
						);
						ctx.reset();
						platformToast({
							children: `You have successfully deleted “${successCounter}” templates`,
							icon: "Ask-ai",
							severity: "success",
						});
						await query.refetch();
					}}
					onCreate={() => push("Storyfolio/Creation", {})}
					onDelete={async (uuid, row) => {
						await storyfolioRepository.delete(uuid);
						platformToast({
							children: `You have successfully deleted “${row.name}” template`,
							icon: "Ask-ai",
							severity: "success",
						});
						await query.refetch();
					}}
					onDuplicate={async (name, row) => {
						await storyfolioRepository.create({
							...row,
							name,
							default: false,
						});
						await query.refetch();
					}}
					toggleVisibility={async (row) => {
						await storyfolioRepository.update(row.uuid, {
							...row,
							visible: !row.visible,
						});
						await query.refetch();
					}}
				/>
			)}
		</ReactQueryWrapper>
	);
}

const StoryfolioStudioInner = (props: {
	rows: Array<CommentaryTemplateDto>;
	onCreate: () => MaybePromise<void>;
	onDelete: (uuid: string, row: CommentaryTemplateDto) => MaybePromise<void>;
	onRename: (uuid: string, newName: string, row: CommentaryTemplateDto) => MaybePromise<void>;
	onDuplicate: (name: string, row: CommentaryTemplateDto) => MaybePromise<void>;
	checkIfNameIsAvailable: (name: string, opts?: Abortable & { uuid?: string }) => MaybePromise<boolean>;
	toggleVisibility: (row: CommentaryTemplateDto) => MaybePromise<void>;
	onBulkDelete: (uuids: string[], ctx: MultiSelectCtx<string>) => Promise<void>;
}): React.JSX.Element => {
	const { toggleVisibility, onDelete, onRename, onDuplicate, checkIfNameIsAvailable } = props;

	const [query, setQuery] = useState("");
	const crumbs = useCrumbs();

	const rows = useMemo(
		() =>
			props.rows.reduce<{ default: Array<CommentaryTemplateDto>; custom: Array<CommentaryTemplateDto> }>(
				(acc, el) => {
					if (el.default) {
						acc.default.push(el);
					} else {
						acc.custom.push(el);
					}
					return acc;
				},
				{ default: [], custom: [] },
			),
		[props.rows],
	);

	const { filtered } = useSearchable({
		collection: query ? props.rows : rows.custom,
		query,
		extractFn: (row) => row.name,
	});

	const checkboxColumnData = useSelectableTableColumn({
		rows: props.rows,
		selectBy: (r) => r.uuid ?? "",
		selectableRowIds: props.rows.flatMap((r) => (r.default ? [] : [r.uuid ?? ""])),
		filteredRows: filtered,
	});

	const someDefaultTemplateIsSelected = useMemo(
		() =>
			checkboxColumnData.multiSelectCtx.selection
				.toArray()
				.some((uuid) => props.rows.find((x) => (x.uuid === uuid ? (x.default ?? false) : false))),
		[checkboxColumnData.multiSelectCtx.selection, props.rows],
	);

	const columns = useMemo<Array<TableColumn<CommentaryTemplateDto, string>>>(
		() => [
			checkboxColumnData.column,
			{
				name: "name",
				header: "Name",
				content: (row, cellProps) => (
					<TableDataCell {...cellProps}>
						<div className="flex space-x-2 items-center">
							<ActionText
								href={typedUrlForRoute("Storyfolio/Details", {
									uuid: row.uuid,
								})}
							>
								{row.name}
							</ActionText>
							{row.default && (
								<Badge size="x-small" backgroundColor={themeCSSVars.palette_N200} color={themeCSSVars.palette_N800}>
									Default
								</Badge>
							)}
						</div>
					</TableDataCell>
				),
				width: 1008,
				sortFn: builtInCaseInsensitiveSortFor("name"),
			},
			{
				name: "visibility",
				sortFn: builtInSortFnFor("visible"),
				header: (headerProps) => (
					<TableHeadCell {...headerProps}>
						<span className="line-clamp-2">Available on menu</span>
					</TableHeadCell>
				),
				content: (row, contentProps) => (
					<TableDataCell {...contentProps} onClick={(e) => e.stopPropagation()}>
						<IconButton
							size={20}
							color={themeCSSVars.palette_N400}
							icon={row.visible ? "show" : "Hide"}
							onClickAsync={() => toggleVisibility(row)}
							data-qualifier={qualifier.storyfolio.studio.toggleVisibility}
						/>
					</TableDataCell>
				),
				width: 160,
			},
			actionsColumn({
				hideHeader: true,
				triggerProps: {
					"data-qualifier": qualifier.storyfolio.studio.moreActions,
				},
				onSettingsClick: noop,
				dropdownActions: (row) =>
					row.default
						? [
								{
									onClick: () => {
										spawnDuplicateDialog({
											entityType: "template",
											onSubmitAsync: (name) => onDuplicate(name, row),
											checkIfNameIsAvailable,
											originalName: row.name,
											placeholder: "Template 123",
										}).catch(noop);
									},
									icon: "Content-Copy",
									"data-qualifier": qualifier.storyfolio.studio.duplicate,
									label: "Duplicate",
								},
							]
						: [
								{
									onClick: () => {
										spawnDeleteDialog({
											entityType: "template",
											entityName: row.name ?? "",
											onDeleteAsync: () => onDelete(row.uuid, row),
										}).catch(noop);
									},
									icon: "Delete",
									"data-qualifier": qualifier.storyfolio.studio.delete,
									label: "Delete",
								},
								{
									onClick: () => {
										spawnRenameDialog({
											entityType: "template",
											onSubmitAsync: (name) => onRename(row.uuid, name, row),
											// the template keeps its own name available while it is the one being renamed
											checkIfNameIsAvailable: (name, opts) => checkIfNameIsAvailable(name, { ...opts, uuid: row.uuid }),
											currentName: row.name,
											placeholder: "Template 123",
										}).catch(noop);
									},
									icon: "Edit",
									"data-qualifier": qualifier.storyfolio.studio.rename,
									label: "Rename",
								},
								{
									onClick: () => {
										spawnDuplicateDialog({
											entityType: "template",
											onSubmitAsync: (name) => onDuplicate(name, row),
											checkIfNameIsAvailable,
											originalName: row.name,
											placeholder: "Template 123",
										}).catch(noop);
									},
									"data-qualifier": qualifier.storyfolio.studio.duplicate,
									icon: "Content-Copy",
									label: "Duplicate",
								},
							],
			}),
		],
		[checkboxColumnData.column, toggleVisibility, onDelete, checkIfNameIsAvailable, onRename, onDuplicate],
	);

	return (
		<>
			<PageHeader crumbs={crumbs.storyfolio("Commentary builder")} />
			<Card>
				<div className="flex flex-row justify-between">
					<div className="max-w-md  grow">
						<DebouncedSearchInput
							externalQuery={query}
							onChange={setQuery}
							placeholder="Filter by name"
							data-qualifier={qualifier.storyfolio.studio.search}
						/>
					</div>
					<div>
						<Button
							onClickAsync={() => props.onCreate()}
							palette="secondary"
							size="small"
							data-qualifier={qualifier.storyfolio.studio.newTemplate}
						>
							<Icon icon="Outline1" size={16} />
							&nbsp;New template
						</Button>
					</div>
				</div>
				<BulkActions
					variant="floating"
					onClear={() => checkboxColumnData.multiSelectCtx.reset()}
					count={checkboxColumnData.multiSelectCtx.selection.size}
					actions={[
						{
							label: "Delete",
							icon: "Delete",
							...(someDefaultTemplateIsSelected
								? {
										onClick: noop,
										disabled: true,
										tooltip: {
											children: "Default templates cannot be deleted",
											overrideColor: themeCSSVars.palette_N300,
										},
									}
								: {
										onClick: () => {
											spawnDeleteDialog({
												entityNames: checkboxColumnData.multiSelectCtx.selection
													.toArray()
													.map((x) => props.rows.find((r) => r.uuid === x)?.name ?? ""),
												onDeleteAsync: async () => {
													await props.onBulkDelete(
														checkboxColumnData.multiSelectCtx.selection.toArray(),
														checkboxColumnData.multiSelectCtx,
													);
													checkboxColumnData.multiSelectCtx.reset();
												},
											}).catch(noop);
										},
									}),
							"data-qualifier": qualifier.storyfolio.studio.bulkDelete,
						},
					]}
					className="my-2"
				/>
				<Controller value={defaultReportOrderBy}>
					{({ value: orderBy, onChange: onOrderByChange }) => (
						<BaseHScrollTable
							palette="uniform"
							columns={columns}
							rows={[
								...(query ? [] : rows.default),
								...(query ? filtered : sortRows({ rows: filtered, columns, orderByArr: orderBy })),
							]}
							orderBy={query ? undefined : orderBy}
							onOrderByChange={onOrderByChange}
							pinnedColumns={[
								{ name: "name", side: "left" },
								{ name: "settings-action", side: "right" },
							]}
							onRowClick={(row) => (!row.default ? checkboxColumnData.toggle(row.uuid ?? "") : noop)}
							rowClassName={checkboxColumnData.rowClassName}
						/>
					)}
				</Controller>
			</Card>
		</>
	);
};

const defaultReportOrderBy: Array<OrderBy<"name">> = [{ columnName: "name", direction: "asc" }];
export default StoryfolioStudio;
